// Site collection curation: which collections the landing page features.
// The admin wallet holder edits this through /admin; the file is the source
// of truth and is read server-side at request time (never in the browser).
//
// NOTE: this module must stay free of Node built-ins (fs/path) because the
// admin page imports it into the client bundle. Server-only file reading
// lives in utils/siteConfig.server.ts.

export const CONFIG_PATH = 'config/collections.json'

// Admin wallets (checksummed or lowercase — compared case-insensitively).
export const ADMIN_WALLETS = [
  '0xFA5b602B211D4CB638254920fD6A79A5C8A7C7Cb',
].map((a) => a.toLowerCase())

export const isAdminWallet = (address?: string | null): boolean =>
  !!address && ADMIN_WALLETS.includes(address.toLowerCase())

export type CollectionArtwork = { image?: string; banner?: string }

// One unified branded set (not split per chain). Each entry carries its own
// chain toggle: the toggle indicates which chain the collection lives on,
// so the landing page fetches its data from that chain's API and links to
// that chain's collection page.
export type BrandedEntry = { address: string; chain: string }

export type SiteCollections = {
  featured: Record<string, string>
  branded: BrandedEntry[]
  artwork?: Record<string, CollectionArtwork>
}

// Server-side file reading lives in utils/siteConfig.server.ts so this
// module stays importable from client components.

// Shared validation for admin writes: every value must be a 0x address.
const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/

export function validateSiteCollections(input: any): {
  ok: boolean
  error?: string
  value?: SiteCollections
} {
  if (!input || typeof input !== 'object')
    return { ok: false, error: 'Config must be an object.' }
  const featured: Record<string, string> = {}
  const branded: BrandedEntry[] = []
  const fIn = input.featured || {}
  const bIn = input.branded || []
  if (typeof fIn !== 'object' || Array.isArray(fIn))
    return { ok: false, error: 'featured must be an object.' }
  for (const [chain, addr] of Object.entries(fIn)) {
    if (typeof addr !== 'string' || !ADDRESS_RE.test(addr))
      return { ok: false, error: `Bad featured address for ${chain}.` }
    featured[chain] = addr
  }
  // Branded is one unified list of {address, chain}. The legacy per-chain
  // object form {ethereum: [...], polygon: [...]} is normalized, not
  // rejected, so old payloads keep working.
  const bList: any[] = Array.isArray(bIn)
    ? bIn
    : typeof bIn === 'object'
    ? Object.entries(bIn).flatMap(([chain, list]: [string, any]) =>
        (Array.isArray(list) ? list : []).map((address: any) => ({
          address,
          chain,
        }))
      )
    : []
  if (!Array.isArray(bIn) && (typeof bIn !== 'object' || bIn === null))
    return { ok: false, error: 'branded must be a list.' }
  const seen: string[] = []
  for (const entry of bList) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry))
      return { ok: false, error: 'Bad branded entry.' }
    const address = entry.address
    const chain =
      typeof entry.chain === 'string' ? entry.chain.toLowerCase() : ''
    if (typeof address !== 'string' || !ADDRESS_RE.test(address))
      return { ok: false, error: `Bad branded address: ${address}.` }
    if (!chain)
      return { ok: false, error: `Bad branded chain for ${address}.` }
    const key = address.toLowerCase()
    if (seen.includes(key)) continue
    seen.push(key)
    branded.push({ address, chain })
  }
  // Optional artwork overrides keyed by lowercase contract address. Imported
  // from OpenSea; fills image/banner gaps Alchemy doesn't have.
  const artwork: Record<string, CollectionArtwork> = {}
  const aIn = input.artwork || {}
  if (typeof aIn !== 'object' || Array.isArray(aIn))
    return { ok: false, error: 'artwork must be an object.' }
  for (const [addr, art] of Object.entries(aIn)) {
    if (!ADDRESS_RE.test(addr))
      return { ok: false, error: `Bad artwork address: ${addr}.` }
    if (!art || typeof art !== 'object' || Array.isArray(art))
      return { ok: false, error: `Bad artwork entry for ${addr}.` }
    const entry: CollectionArtwork = {}
    for (const k of ['image', 'banner'] as const) {
      const v = (art as any)[k]
      if (v == null || v === '') continue
      if (typeof v !== 'string' || !/^https:\/\//.test(v))
        return { ok: false, error: `Bad artwork ${k} for ${addr}.` }
      entry[k] = v
    }
    if (entry.image || entry.banner) artwork[addr.toLowerCase()] = entry
  }
  return { ok: true, value: { featured, branded, artwork } }
}

// The exact message the admin wallet signs when publishing a config change.
export const adminSignMessage = (configJson: string): string =>
  `audi0 admin — publish site collections:\n${configJson}`
